import React, { useEffect, useState, useRef, useCallback } from "react";
import ApiService from "../services/ApiService";
import Tooltip from "../components/Tooltip";
import SkeletonLoader from "../components/SkeletonLoader";

export default function AccountOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedProducts, setExpandedProducts] = useState({});
    const [tooltipData, setTooltipData] = useState({ visible: false, model: null, position: { x: 0, y: 0 } });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25);
    const [visibleCounts, setVisibleCounts] = useState({});
    const containerRefs = useRef({});
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showGross, setShowGross] = useState(true);

    const openOrderModal = (order) => {
        setSelectedOrder(order);
        document.body.classList.add('modal-open');
    };

    const closeOrderModal = () => {
        setSelectedOrder(null);
        document.body.classList.remove('modal-open');
    };

    const handleInvoiceClick = (order) => {
        if (order.invoice_url) {
            // Otwórz link do faktury w nowym oknie
            window.open(order.invoice_url, '_blank', 'noopener,noreferrer');
        } else {
            alert('Faktura jeszcze nie jest dostępna, sprawdź po zakończeniu zamówienia.');
        }
    };

    const getDisplayStatus = (paymentStatus, orderStatus) => {
        // Zabezpieczenie dla brakujących danych
        const payment = paymentStatus || 'pending';
        const order = orderStatus || 'new';
        const key = `${payment}/${order}`;

        const statusMapping = {
            'pending/new': { label: "Oczekuje na płatność", color: "#ff9800" },
            'pending/approved': { label: "Oczekuje na płatność", color: "#ff9800" },
            'pending/shipped': { label: "Oczekuje na płatność", color: "#ff9800" },
            'pending/completed': { label: "Oczekuje na płatność", color: "#ff9800" },
            'paid/new': { label: "Opłacone - weryfikacja techniczna", color: "#2196f3" },
            'paid/approved': { label: "W realizacji", color: "#9c27b0" },
            'paid/shipped': { label: "Wysłane", color: "#607d8b" },
            'paid/completed': { label: "Zrealizowane", color: "#4caf50" },
            'failed/new': { label: "Płatność nieudana", color: "#f44336" },
            'refunded/new': { label: "Zwrócono środki", color: "#795548" }
        };

        return statusMapping[key] || { label: "Oczekuje na płatność", color: "#ff9800" };
    };


    // Funkcja obliczająca ile miniaturek się zmieści w kontenerze
    const calculateVisibleImages = useCallback((orderId, models) => {
        const container = containerRefs.current[orderId];
        if (!container || !models?.length) return models?.length || 0;

        const containerWidth = container.offsetWidth;
        const imageWidth = 56; // width z CSS: .product-image
        const gap = 4; // gap z CSS: .product-images-container
        const buttonWidth = 56; // width z CSS: .product-expand-btn
        const buttonGap = 4;

        if (containerWidth < imageWidth) return 1;

        const maxWithoutButton = Math.floor((containerWidth + gap) / (imageWidth + gap));

        if (models.length <= maxWithoutButton) {
            return models.length;
        }

        // Oblicz ile się zmieści z przyciskiem "+X więcej"
        const availableForImages = containerWidth - buttonWidth - buttonGap;
        const maxWithButton = Math.floor((availableForImages + gap) / (imageWidth + gap));

        return Math.max(1, maxWithButton);
    }, []);

    useEffect(() => {
        const fetchOrders = async () => {
            setLoading(true);
            try {
                const response = await ApiService.getOrders();
                setOrders(response.orders);
            } catch (err) {
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    useEffect(() => {
        if (orders.length === 0) return;

        const observer = new ResizeObserver(() => {
            getPaginatedOrders().forEach(order => {
                if (order.orderData?.models) {
                    const newCount = calculateVisibleImages(order.id, order.orderData.models);
                    setVisibleCounts(prev => ({
                        ...prev,
                        [order.id]: newCount
                    }));
                }
            });
        });

        // Wait for DOM to render
        setTimeout(() => {
            Object.values(containerRefs.current).forEach(ref => {
                if (ref) observer.observe(ref);
            });

                // Initial calculation
                getPaginatedOrders().forEach(order => {
                    if (order.orderData?.models) {
                        const count = calculateVisibleImages(order.id, order.orderData.models);
                        setVisibleCounts(prev => ({
                            ...prev,
                            [order.id]: count
                        }));
                    }
                });
        }, 200);

        return () => observer.disconnect();
    }, [orders, currentPage, calculateVisibleImages]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const toggleExpandProducts = (orderId) => {
        setExpandedProducts(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const handleTooltipShow = (model, event) => {
        setTooltipData({
            visible: true,
            model,
            position: { x: event.clientX, y: event.clientY }
        });
    };

    const handleTooltipHide = () => {
        setTooltipData({ visible: false, model: null, position: { x: 0, y: 0 } });
    };

    const renderProductImages = (models, orderId) => {
        const maxVisible = visibleCounts[orderId] || 6;
        const visibleModels = models.slice(0, maxVisible);
        const hiddenCount = models.length - maxVisible;
        const isExpanded = expandedProducts[orderId];

        return (
            <div>
            <div
            className="product-images-container"
            ref={el => containerRefs.current[orderId] = el}
            >
            {visibleModels.map((model, index) => (
                <img
                key={index}
                src={`${process.env.REACT_APP_BACKEND_URL}${model.thumbnailUrl}`}
                alt={model.name}
                className="product-image"
                onMouseEnter={(e) => handleTooltipShow(model, e)}
                onMouseLeave={handleTooltipHide}
                />
            ))}

            {hiddenCount > 0 && (
                <button
                onClick={() => toggleExpandProducts(orderId)}
                className="product-expand-btn"
                >
                +{hiddenCount}
                </button>
            )}
            </div>

            {isExpanded && hiddenCount > 0 && (
                <div className="product-expanded-images">
                {models.slice(maxVisible).map((model, index) => (
                    <img
                    key={index + maxVisible}
                    src={`${process.env.REACT_APP_BACKEND_URL}${model.thumbnailUrl}`}
                    alt={model.name}
                    className="product-image"
                    onMouseEnter={(e) => handleTooltipShow(model, e)}
                    onMouseLeave={handleTooltipHide}
                    />
                ))}
                </div>
            )}
            </div>
        );
    };

    const getPaginatedOrders = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return orders.slice(startIndex, endIndex);
    };

    const getTotalPages = () => Math.ceil(orders.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        const totalPages = getTotalPages();
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        if (startPage > 1) {
            pages.push(
                <button key="first" onClick={() => handlePageChange(1)} className="pagination-btn">1</button>
            );
            if (startPage > 2) {
                pages.push(<span key="dots1" className="pagination-dots">...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                key={i}
                onClick={() => handlePageChange(i)}
                className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
                >
                {i}
                </button>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<span key="dots2" className="pagination-dots">...</span>);
            }
            pages.push(
                <button key="last" onClick={() => handlePageChange(totalPages)} className="pagination-btn">{totalPages}</button>
            );
        }

        return (
            <div className="pagination-container">
            <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn pagination-prev"
            >
            ← Poprzednia
            </button>
            {pages}
            <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn pagination-next"
            >
            Następna →
            </button>
            </div>
        );
    };

    if (loading) {
        return <SkeletonLoader type="orders-table" />;
    }

    return (
        <div>
        <table className="orders-table">
        <thead>
        <tr className="orders-table-header">
        <th className="orders-table-title" colSpan="6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Moje zamówienia</span>
        <label style={{ fontSize: '0.9rem', fontWeight: 'normal', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
        type="checkbox"
        checked={showGross}
        onChange={(e) => setShowGross(e.target.checked)}
        style={{ cursor: 'pointer' }}
        />
        Wyświetlaj ceny brutto
        </label>
        </div>
        </th>
        </tr>
        <tr className="orders-table-header">
        <th className="orders-table-column-header" style={{ width: '120px' }}>
        Numer zamówienia
        </th>
        <th className="orders-table-column-header">
        Zamówione produkty
        </th>
        <th className="orders-table-column-header" style={{ width: '160px' }}>
        Data złożenia zamówienia
        </th>
        <th className="orders-table-column-header" style={{ width: '100px' }}>
        {showGross ? 'Wartość brutto' : 'Wartość netto (bez VAT)'}
        </th>
        <th className="orders-table-column-header" style={{ width: '120px' }}>
        Status
        </th>
        <th className="orders-table-column-header" style={{ width: '80px' }}>
        Faktura
        </th>
        </tr>
        </thead>
        <tbody>
        {orders.length === 0 ? (
            <tr>
            <td colSpan="6" className="orders-table-empty">
            Brak zamówień
            </td>
            </tr>
        ) : (
            getPaginatedOrders().map(order => (
                <tr key={order.id} className="orders-table-row">
                <td className="orders-table-cell">
                <a
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    openOrderModal(order);
                }}
                className="order-link"
                >
                {order.id}
                </a>
                </td>
                <td className="orders-table-cell">
                {order.orderData?.models && renderProductImages(order.orderData.models, order.id)}
                </td>
                <td className="orders-table-cell">
                {formatDate(order.date)}
                </td>
                <td className="orders-table-cell" style={{ fontWeight: 'bold' }}>
                {showGross
                    ? (order.orderData?.pricing?.partsGross || order.total || 0).toFixed(2)
                    : (order.orderData?.pricing?.partsNet || order.total || 0).toFixed(2)
                } PLN
                </td>
                <td className="orders-table-cell">
                <span className="status-badge" style={{ backgroundColor: getDisplayStatus(order.payment_status, order.status).color }}>
                {getDisplayStatus(order.payment_status, order.status).label}
                </span>
                </td>
                <td className="orders-table-cell" style={{ textAlign: 'center' }}>
                <span
                    onClick={() => handleInvoiceClick(order)}
                    title={order.invoice_url ? "Pobierz fakturę" : "Faktura niedostępna"}
                    style={{
                        cursor: 'pointer',
                        color: order.invoice_url ? '#00b8a9' : '#ccc',
                        fontSize: '1.2rem'
                    }}
                >
                📄
                </span>
                </td>
                </tr>
            ))
        )}
        </tbody>
        </table>

        {renderPagination()}

        {tooltipData.visible && tooltipData.model && (
            <div
            style={{
                position: 'fixed',
                top: tooltipData.position.y + 10,
                left: tooltipData.position.x + 10,
                zIndex: 1000
            }}
            >
            <Tooltip
            title={tooltipData.model.name}
            description={
                <div>
                <div><strong>Technologia:</strong> {tooltipData.model.technology}</div>
                <div><strong>Materiał:</strong> {tooltipData.model.material}</div>
                <div><strong>Kolor:</strong> {tooltipData.model.color}</div>
                <div><strong>Wypełnienie:</strong> {tooltipData.model.infill}%</div>
                <div><strong>Ilość:</strong> {tooltipData.model.quantity} szt.</div>
                <div><strong>Cena jednostkowa:</strong> {tooltipData.model.price} PLN</div>
                <div><strong>Wymiary:</strong> {tooltipData.model.dimensions.x}×{tooltipData.model.dimensions.y}×{tooltipData.model.dimensions.z} cm</div>
                </div>
            }
            onClose={handleTooltipHide}
            />
            </div>
        )}



        {/* Modal szczegółów zamówienia */}
        {selectedOrder && (
            <div className="order-modal-overlay" onClick={closeOrderModal}>
            <div className="order-modal" onClick={(e) => e.stopPropagation()}>

            <div className="order-modal-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <h2>Szczegóły zamówienia #{selectedOrder.id}</h2>
            <label style={{ fontSize: '0.9rem', fontWeight: 'normal', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
            type="checkbox"
            checked={showGross}
            onChange={(e) => setShowGross(e.target.checked)}
            style={{ cursor: 'pointer' }}
            />
            Wyświetlaj ceny brutto
            </label>
            </div>
            <button onClick={closeOrderModal} className="modal-close-button">×</button>
            </div>

            <div className="order-modal-content">
            {/* Sekcja modeli */}
            <div className="order-section">
            <h3>Modele do wydruku ({selectedOrder.orderData?.models?.length || 0})</h3>
            <table className="models-table">

            <thead>
            <tr>
            <th>Miniaturka</th>
            <th>Nazwa pliku</th>
            <th>Technologia</th>
            <th>Tworzywo</th>
            <th>Wypełnienie</th>
            <th>Nakład</th>
            <th>{showGross ? 'Cena jedn. brutto' : 'Cena jedn. netto'}</th>
            <th>{showGross ? 'Suma brutto' : 'Suma netto'}</th>
            </tr>
            </thead>

            <tbody>
            {selectedOrder.orderData?.models?.map((model, index) => (
                <tr key={index}>
                <td>
                <img
                src={`${process.env.REACT_APP_BACKEND_URL}${model.thumbnailUrl}`}
                alt={model.name}
                className="model-thumbnail"
                />
                </td>
                <td className="model-name">{model.name}</td>
                <td className="model-tech">{model.technology}</td>
                <td className="model-material">
                {model.material} • {model.color}
                </td>
                <td className="model-infill">{model.infill}%</td>
                <td className="model-quantity">{model.quantity} szt.</td>

                <td className="model-price">
                {showGross
                    ? (model.priceGross || model.price || 0).toFixed(2)
                    : (model.priceNet || model.price || 0).toFixed(2)
                } PLN
                </td>
                <td className="model-total">
                {showGross
                    ? (model.totalGross || (model.price * model.quantity) || 0).toFixed(2)
                    : (model.totalNet || (model.price * model.quantity) || 0).toFixed(2)
                } PLN
                </td>

                </tr>
            )) || []}
            </tbody>
            </table>
            </div>

            {/* Sekcja informacji o zamówieniu */}
            <div className="order-section">
            <h3>Informacje o zamówieniu</h3>
            <div className="order-info">
            <div className="data-row">
            <span className="label">Data złożenia:</span>
            <span>{formatDate(selectedOrder.date)}</span>
            </div>
            <div className="data-row">
            <span className="label">Status:</span>
            <span className="status-badge" style={{ backgroundColor: getDisplayStatus(selectedOrder.payment_status, selectedOrder.status).color }}>
            {getDisplayStatus(selectedOrder.payment_status, selectedOrder.status).label}
            </span>
            </div>
            <div className="data-row">
            <span className="label">Czas realizacji:</span>
            <span>{selectedOrder.orderData?.leadTimeDays || 'Brak'} dni roboczych</span>
            </div>
            {selectedOrder.orderData?.note && (
                <div className="data-row">
                <span className="label">Notatka do zamówienia:</span>
                <span style={{ fontStyle: 'italic', color: '#555' }}>{selectedOrder.orderData.note}</span>
                </div>
            )}
            </div>
            </div>

            {/* Sekcja dostawy */}
            <div className="order-section">
            <h3>Dostawa</h3>
            <div className="shipping-info">
            <div className="data-row">
            <span className="label">Metoda:</span>
            <span>{selectedOrder.orderData?.shipping?.method || 'Brak'}</span>
            </div>
            {selectedOrder.orderData?.shipping?.method === 'paczkomat' ? (
                <>
                <div className="data-row">
                <span className="label">Paczkomat:</span>
                <span>{selectedOrder.orderData.shipping.paczkomat || 'Brak'}</span>
                </div>
                <div className="data-row">
                <span className="label">Adres paczkomatu:</span>
                <span>{selectedOrder.orderData.shipping.paczkomatAddress || 'Brak'}</span>
                </div>
                </>
            ) : (
                <div className="data-row">
                <span className="label">Adres dostawy:</span>
                <span>
                {selectedOrder.orderData?.client?.address || 'Brak'}<br/>
                {selectedOrder.orderData?.client?.postcode} {selectedOrder.orderData?.client?.city}<br/>
                {selectedOrder.orderData?.client?.country || 'Polska'}
                </span>
                </div>
            )}
            <div className="data-row">
            <span className="label">Koszt dostawy:</span>
            <span>
            {(selectedOrder.orderData?.shipping?.shippingGross || 0).toFixed(2)} PLN brutto
            (w tym {(selectedOrder.orderData?.shipping?.shippingVat || 0).toFixed(2)} PLN VAT)
            </span>
            </div>
            </div>
            </div>

            {/* Sekcja danych do faktury - tylko dla firm */}
            {selectedOrder.orderData?.client?.company_name && (
                <div className="order-section">
                <h3>Dane do faktury</h3>
                <div className="company-info">
                <div className="data-row">
                <span className="label">Nazwa firmy:</span>
                <span>{selectedOrder.orderData.client.company_name}</span>
                </div>
                <div className="data-row">
                <span className="label">NIP:</span>
                <span>{selectedOrder.orderData.client.company_nip || 'Brak'}</span>
                </div>
                <div className="data-row">
                <span className="label">Adres firmy:</span>
                <span>
                {selectedOrder.orderData.client.company_address || 'Brak'}<br/>
                {selectedOrder.orderData.client.company_postcode} {selectedOrder.orderData.client.company_city}<br/>
                {selectedOrder.orderData.client.company_country || 'Polska'}
                </span>
                </div>
                </div>
                </div>
            )}

            {/* Sekcja cenowa */}
            <div className="order-section">
            <h3>Podsumowanie</h3>
            <div className="pricing-summary">

            {showGross ? (
                <>
                <div className="data-row">
                <span className="label">Suma części:</span>
                <span>{(selectedOrder.orderData?.pricing?.partsGross || 0).toFixed(2)} PLN</span>
                </div>
                {(selectedOrder.orderData?.pricing?.partsNet || 0) <= 25 && (
                    <div className="data-row" style={{ color: '#00b8a9', fontWeight: '500' }}>
                    <span className="label">Minimalna wartość:</span>
                    <span>30.75 PLN brutto</span>
                    </div>
                )}
                <div className="data-row">
                <span className="label">w tym VAT:</span>
                <span>{(selectedOrder.orderData?.pricing?.partsVat || 0).toFixed(2)} PLN</span>
                </div>
                <div className="data-row total-row">
                <span className="label">Do zapłaty:</span>
                <span>{((selectedOrder.orderData?.pricing?.partsNet || 0) <= 25 ? 30.75 : (selectedOrder.orderData?.pricing?.partsGross || 0)).toFixed(2)} PLN</span>
                </div>
                </>
            ) : (
                <>
                <div className="data-row">
                <span className="label">Suma części (netto):</span>
                <span>{(selectedOrder.orderData?.pricing?.partsNet || 0).toFixed(2)} PLN</span>
                </div>
                {(selectedOrder.orderData?.pricing?.partsNet || 0) <= 25 && (
                    <div className="data-row" style={{ color: '#00b8a9', fontWeight: '500' }}>
                    <span className="label">Minimalna wartość:</span>
                    <span>25.00 PLN netto</span>
                    </div>
                )}
                <div className="data-row">
                <span className="label">Suma VAT:</span>
                <span>{(selectedOrder.orderData?.pricing?.partsVat || 0).toFixed(2)} PLN</span>
                </div>
                <div className="data-row total-row">
                <span className="label">Do zapłaty:</span>
                <span>{((selectedOrder.orderData?.pricing?.partsNet || 0) <= 25 ? 30.75 : (selectedOrder.orderData?.pricing?.partsGross || 0)).toFixed(2)} PLN</span>
                </div>
                </>
            )}

            </div>
            </div>

            </div>




            </div>
            </div>
        )}
        </div>
    );
}
